"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stages = [{
  numeral: "I",
  month: "PHASE 01 · ANALYSIS",
  title: "Jurisdictional Audit",
  description: "We begin by conducting a comprehensive review of your current asset landscape. Our attorneys analyze existing holdings, tax liabilities, and long-term objectives to establish a foundation for your customized trust architecture.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/legal_audit_process.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/legal_audit_process.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/legal_audit_process.png-wvc-sizes",
  alt: "Legal documents and analytical charts on a mahogany desk"
}, {
  numeral: "II",
  month: "PHASE 02 · DESIGN",
  title: "Structural Blueprinting",
  description: "Our team drafts bespoke trust instruments tailored to your specific needs. We integrate advanced asset protection strategies and estate planning mechanisms designed to withstand rigorous scrutiny and ensure generational continuity.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/trust_design_blueprint.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/trust_design_blueprint.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/trust_design_blueprint.webp-wvc-sizes",
  alt: "Architectural drafting tools and legal trust documents"
}, {
  numeral: "III",
  month: "PHASE 03 · COUNSEL",
  title: "Sovereign Advisory",
  description: "We provide informed guidance on the complexities of sovereign citizenship. Our counsel helps you navigate jurisdictional questions, assert your rights responsibly, and understand the legal implications of your status.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/sovereign_counsel_session.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/sovereign_counsel_session.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/sovereign_counsel_session.png-wvc-sizes",
  alt: "Professional legal consultation in a private office"
}, {
  numeral: "IV",
  month: "PHASE 04 · EXECUTION",
  title: "Formal Implementation",
  description: "The finalized legal structures are formally executed and registered. We oversee the transfer of assets into the trust, ensuring every detail aligns with the established strategy and regulatory requirements.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/legal_execution_signing.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/legal_execution_signing.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/legal_execution_signing.png-wvc-sizes",
  alt: "Formal signing of legal trust instruments"
}, {
  numeral: "V",
  month: "PHASE 05 · OVERSIGHT",
  title: "Continuous Governance",
  description: "Legal landscapes evolve, and so must your strategy. We provide ongoing oversight, conducting periodic reviews to ensure your trust remains robust, compliant, and perfectly aligned with your shifting life goals.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/legal_governance_review.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/legal_governance_review.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/legal_governance_review.png-wvc-sizes",
  alt: "Attorney reviewing legal files in a library"
}, {
  numeral: "VI",
  month: "PHASE 06 · LEGACY",
  title: "Generational Security",
  description: "The final stage is the preservation of your legacy. We ensure that your assets are protected for future generations, providing the peace of mind that comes with meticulous, professional legal stewardship.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/legacy_preservation_vault.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/legacy_preservation_vault.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699670/img/legacy_preservation_vault.webp-wvc-sizes",
  alt: "Secure vault door representing asset protection"
}];
export default function TheLegacyRoadmap() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const medallion = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <section data-section-id="3180" id="the-legacy-roadmap" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
      backgroundImage: "radial-gradient(circle at 20% 30%, var(--foreground) 1px, transparent 1px), radial-gradient(circle at 70% 60%, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "40px 40px, 60px 60px"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        {}
        <div ref={header.ref} className={`max-w-3xl transition-all duration-1000 ease-out ${header.isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <div className="flex items-center gap-4">
            <span className="font-mono text-sm tracking-widest text-foreground/70">
              02 —
            </span>
            <span className="h-px w-12 bg-primary/60" />
            <span className="font-mono text-xs tracking-[0.3em] text-primary uppercase">
              The Methodology
            </span>
          </div>

          <h2 className="font-default mt-8 text-4xl leading-[1.05] tracking-tight md:text-6xl lg:text-[64px]">
            The VESTA <em className="italic text-primary">Roadmap</em>
          </h2>

          <p className="font-default mt-8 max-w-[600px] text-base leading-relaxed text-foreground/75 md:text-lg">
            Our structured approach to trust law and sovereign counsel ensures 
            meticulous protection and clarity for your financial future.
          </p>
        </div>

        {}
        <div className="relative mt-20 md:mt-28">
          {}
          <div className="absolute top-0 bottom-0 left-6 w-px bg-gradient-to-b from-transparent via-primary/60 to-transparent md:left-1/2 md:-translate-x-1/2" aria-hidden="true" />

          <div className="flex flex-col gap-20 md:gap-28">
            {stages.map((stage, i) => {
            const isLeft = i % 2 === 0;
            return <TimelineNode key={stage.numeral} data-index={i} stage={stage} isLeft={isLeft} />;
          })}
          </div>

          {}
          <div ref={medallion.ref} className={`relative mt-24 flex flex-col items-center transition-all duration-1000 ease-out md:mt-32 ${medallion.isVisible ? "translate-y-0 opacity-100 scale-100" : "translate-y-6 opacity-0 scale-95"}`}>
            {}
            <div className="absolute -top-20 left-6 h-20 w-px bg-gradient-to-b from-primary/60 to-primary md:left-1/2 md:-translate-x-1/2" aria-hidden="true" />

            <div className="relative ml-12 md:ml-0">
              {}
              <div className="relative flex h-44 w-44 items-center justify-center rounded-lg md:h-52 md:w-52">
                <div className="absolute inset-0 rounded-lg border border-primary/50" style={{
                borderRadius: "0px"
              }} aria-hidden="true" />
                <div className="absolute inset-3 border border-primary/30" style={{
                borderRadius: "0px"
              }} aria-hidden="true" />
                {}
                <div className="relative flex h-32 w-32 flex-col items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5 backdrop-blur-sm md:h-36 md:w-36" style={{
                borderRadius: "0px"
              }}>
                  <span className="font-mono text-[10px] tracking-[0.25em] text-primary uppercase">
                    Secured
                  </span>
                  <span className="font-default mt-1 text-xl italic text-foreground md:text-2xl">
                    Legacy
                  </span>
                  <div className="mt-2 flex items-center gap-1">
                    <span className="h-px w-3 bg-primary/60" />
                    <span className="font-mono text-[10px] text-primary">
                      VI
                    </span>
                    <span className="h-px w-3 bg-primary/60" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
type StageType = (typeof stages)[number];
function TimelineNode({
  stage,
  isLeft,
  ...rest
}: {
  stage: StageType;
  isLeft: boolean;
  "data-index"?: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} {...rest} className="relative grid grid-cols-1 items-center md:grid-cols-12 md:gap-8">
      {}
      <div className={`absolute left-6 z-10 -translate-x-1/2 md:left-1/2 transition-all duration-700 ease-out ${isVisible ? "scale-100 opacity-100" : "scale-50 opacity-0"}`} aria-hidden="true">
        <div className="relative flex h-6 w-6 items-center justify-center rounded-lg" style={{
        borderRadius: "0px"
      }}>
          <div className="absolute inset-0 bg-primary" style={{
          borderRadius: "0px"
        }} />
          <div className="absolute inset-0 bg-primary/40 blur-md" style={{
          borderRadius: "0px"
        }} aria-hidden="true" />
          <span className="relative font-mono text-[9px] font-semibold text-primary-foreground">
            {stage.numeral}
          </span>
        </div>
      </div>

      {}
      <div className={`pl-16 md:pl-0 ${isLeft ? "md:col-span-5 md:col-start-1 md:pr-8 md:text-right" : "md:col-span-5 md:col-start-8 md:pl-8"} transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : isLeft ? "-translate-x-4 opacity-0" : "translate-x-4 opacity-0"}`}>
        <span className="font-mono inline-block text-[11px] tracking-[0.25em] text-primary uppercase">
          {stage.month}
        </span>
        <h3 className="font-default mt-3 text-2xl leading-tight tracking-tight md:text-3xl lg:text-[32px]">
          {stage.title}
        </h3>
        <p className="font-default mt-4 text-sm leading-relaxed text-foreground/70 md:text-base">
          {stage.description}
        </p>
      </div>

      {}
      <div className={`mt-6 pl-16 md:mt-0 md:pl-0 ${isLeft ? "md:col-span-5 md:col-start-8 md:pl-8" : "md:col-span-5 md:col-start-1 md:pr-8"} transition-all duration-1000 ease-out delay-150 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
        <div className="relative aspect-square w-full max-w-[280px] overflow-hidden rounded-lg border border-border/50 md:max-w-[320px]">
          <img src={stage.image} data-wvc-srcset={stage.srcset} data-wvc-sizes={stage.sizes} alt={stage.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
          {}
          <div className="pointer-events-none absolute inset-0 opacity-30 mix-blend-overlay" style={{
          backgroundImage: "radial-gradient(circle at 25% 25%, var(--foreground) 0.5px, transparent 0.5px), radial-gradient(circle at 75% 75%, var(--foreground) 0.5px, transparent 0.5px)",
          backgroundSize: "3px 3px, 5px 5px"
        }} aria-hidden="true" />
          {}
          <div className="absolute top-3 left-3 flex items-center gap-1.5">
            <span className="h-px w-4 bg-primary" />
            <span className="font-mono text-[10px] tracking-widest text-primary">
              {stage.numeral}
            </span>
          </div>
        </div>
      </div>
    </div>;
}